import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  FileText, 
  Download, 
  Eye,
  Search,
  Filter,
  ChevronUp,
  ChevronDown,
  CreditCard,
  Landmark,
  Users,
  Building,
  FileDown,
  ArchiveIcon,
  RefreshCw,
  Send,
  Plus,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { RaiseQueryButton, QueryBadge } from '@/components/InvoiceQuery';
import { determineGstType, getEntityState, getEntityGstin, gstBreakdownLines } from '@/utils/gstUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BILLING_CYCLES = ['monthly','quarterly','half-yearly','yearly','2 Year','3 Year'];
// Map display label → DB key (used for enabled_billing_cycles lookup)
const BILLING_CYCLE_DISPLAY_TO_KEY = { 'monthly': 'monthly', 'quarterly': 'quarterly', 'half-yearly': 'half_yearly', 'yearly': 'yearly', '2 Year': 'two_years', '3 Year': 'three_years' };
const EMPTY_INV_FORM = { category_id: '', product_id: '', product_name: '', billing_cycle: 'monthly', quantity: 1, amount: '', payment_status: 'pending', notes: '' };

export default function ResellerInvoices() {
  const { user } = useAuth();
  const [myInvoices, setMyInvoices] = useState([]);
  const [channelPartnerInvoices, setChannelPartnerInvoices] = useState([]);
  const [clientInvoices, setClientInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('my'); // 'my', 'channel', 'clients'
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);

  // Category + Product
  const [categories, setCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [clientFilteredProducts, setClientFilteredProducts] = useState([]);
  const [cpFilteredProducts, setCpFilteredProducts] = useState([]);

  // Client Invoice dialog
  const [clientInvOpen, setClientInvOpen] = useState(false);
  const [clientInvForm, setClientInvForm] = useState(EMPTY_INV_FORM);
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [creatingClientInv, setCreatingClientInv] = useState(false);
  const [sendingInvId, setSendingInvId] = useState(null);

  // CP Invoice dialog  
  const [cpInvOpen, setCpInvOpen] = useState(false);
  const [cpInvForm, setCpInvForm] = useState(EMPTY_INV_FORM);
  const [cpSearch, setCpSearch] = useState('');
  const [cpResults, setCpResults] = useState([]);
  const [selectedCP, setSelectedCP] = useState(null);
  const [cpClientResults, setCpClientResults] = useState([]);
  const [selectedCPClient, setSelectedCPClient] = useState(null);
  const [cpInvStep, setCpInvStep] = useState(1); // 1 = select CP, 2 = select client + form
  const [creatingCpInv, setCreatingCpInv] = useState(false);
  
  // Sidebar state
  const [invoicesDueExpanded, setInvoicesDueExpanded] = useState(true);
  const [statusExpanded, setStatusExpanded] = useState(true);
  const [billingExpanded, setBillingExpanded] = useState(true);

  // SP's selling prices per product (loaded from /api/reseller/pricing)
  const [spPricingMap, setSpPricingMap] = useState({});  // { prodId: { custom_prices, effective_base_prices } }

  const isSynergyPartner = user?.role === 'reseller';

  // Helper: get SP's selling price for a product + billing cycle
  const getSpSellingPrice = (prodId, cycleKey) => {
    const row = spPricingMap[prodId];
    if (!row) return null;
    return row.custom_pricing?.custom_prices?.add?.[cycleKey]
      ?? row.effective_base_prices?.[cycleKey]
      ?? null;
  };

  useEffect(() => {
    fetchAllInvoices();
    // Load categories + products for the dropdowns
    Promise.all([
      axios.get(`${API}/categories`).catch(() => ({ data: [] })),
      axios.get(`${API}/products`).catch(() => ({ data: [] })),
      axios.get(`${API}/reseller/pricing`).catch(() => ({ data: [] })),
    ]).then(([catRes, prodRes, pricingRes]) => {
      setCategories(catRes.data || []);
      setAllProducts(prodRes.data || []);
      const pm = {};
      for (const row of (pricingRes.data || [])) {
        const pid = row.product?.id || row.product_id;
        if (pid) pm[pid] = row;
      }
      setSpPricingMap(pm);
    });
  }, []);

  const fetchAllInvoices = async () => {
    try {
      const [myRes, channelRes, clientRes] = await Promise.all([
        axios.get(`${API}/invoices`),
        isSynergyPartner ? axios.get(`${API}/invoices/channel-partners`) : Promise.resolve({ data: [] }),
        isSynergyPartner ? axios.get(`${API}/invoices/clients`) : Promise.resolve({ data: [] })
      ]);
      setMyInvoices(myRes.data || []);
      setChannelPartnerInvoices(channelRes.data || []);
      setClientInvoices(clientRes.data || []);
    } catch (error) {
      console.log('Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };

  // Map billing cycle label → billing_prices key
  const getBillingCycleKey = (cycle) => {
    const map = { 'monthly': 'monthly', 'quarterly': 'quarterly', 'half-yearly': 'half_yearly', 'yearly': 'yearly', '2 Year': 'two_years', '3 Year': 'three_years' };
    return map[cycle] || 'monthly';
  };

  // Shared: handle category selection → filter products (matches by category NAME not id)
  const handleCategoryChange = (catId, formSetter, setFilteredForDialog) => {
    formSetter(f => ({ ...f, category_id: catId, product_id: '', product_name: '', amount: '' }));
    if (!catId) {
      setFilteredForDialog([]);
    } else {
      const selectedCat = categories.find(c => c.id === catId);
      const catName = selectedCat?.name || '';
      setFilteredForDialog(catName ? allProducts.filter(p => p.category === catName) : []);
    }
  };

  // Shared: handle product selection → auto-fill name + price for selected billing cycle
  const handleProductChange = (prodId, formSetter, currentBillingCycle) => {
    const prod = allProducts.find(p => (p.id || p._id) === prodId);
    if (prod) {
      const cycleKey = getBillingCycleKey(currentBillingCycle || 'monthly');
      // Use SP's own selling price (from pricing settings) → fallback to admin billing price
      const spPrice = getSpSellingPrice(prod.id || prod._id, cycleKey);
      const price = spPrice ?? prod.billing_prices?.[cycleKey] ?? prod.base_price ?? '';
      formSetter(f => ({ ...f, product_id: prodId, product_name: prod.name, amount: String(price) }));
    } else {
      formSetter(f => ({ ...f, product_id: prodId, product_name: '', amount: '' }));
    }
  };

  // Search clients
  const handleClientSearch = async (q) => {
    setClientSearch(q);
    setSelectedClient(null);
    if (!q || q.length < 2) { setClientResults([]); return; }
    try {
      const res = await axios.get(`${API}/reseller/search-clients?query=${encodeURIComponent(q)}`);
      setClientResults(res.data || []);
    } catch { setClientResults([]); }
  };

  // Search CPs
  const handleCpSearch = async (q) => {
    setCpSearch(q);
    setSelectedCP(null);
    setSelectedCPClient(null);
    setCpClientResults([]);
    if (!q || q.length < 2) { setCpResults([]); return; }
    try {
      const res = await axios.get(`${API}/reseller/search-cps?query=${encodeURIComponent(q)}`);
      setCpResults(res.data || []);
    } catch { setCpResults([]); }
  };

  // Load clients under selected CP
  const handleSelectCP = async (cp) => {
    setSelectedCP(cp);
    setCpSearch(cp.company_name || cp.email);
    setCpResults([]);
    setSelectedCPClient(null);
    try {
      const res = await axios.get(`${API}/reseller/cp-clients/${cp.id}`);
      setCpClientResults(res.data || []);
    } catch { setCpClientResults([]); }
    setCpInvStep(2);
  };

  // === P3: Create invoice ONLY (no mail) ===
  const handleCreateClientInvoiceOnly = async () => {
    if (!selectedClient || !clientInvForm.product_name || !clientInvForm.amount) {
      toast.error('Please fill in all required fields'); return;
    }
    setCreatingClientInv(true);
    try {
      await axios.post(`${API}/reseller/invoices`, {
        invoice_type: 'client', recipient_id: selectedClient.id,
        product_name: clientInvForm.product_name,
        billing_cycle: clientInvForm.billing_cycle,
        quantity: Number(clientInvForm.quantity) || 1,
        amount: Number(clientInvForm.amount),
        payment_status: clientInvForm.payment_status,
        notes: clientInvForm.notes,
      });
      toast.success('Invoice created (not yet sent)');
      setClientInvOpen(false); setSelectedClient(null); setClientSearch('');
      setClientInvForm(EMPTY_INV_FORM); setClientFilteredProducts([]);
      fetchAllInvoices();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create invoice');
    } finally { setCreatingClientInv(false); }
  };

  // === P3: Create & Send invoice ===
  const handleCreateClientInvoice = async () => {
    if (!selectedClient || !clientInvForm.product_name || !clientInvForm.amount) {
      toast.error('Please fill in all required fields'); return;
    }
    setCreatingClientInv(true);
    try {
      const res = await axios.post(`${API}/reseller/invoices`, {
        invoice_type: 'client', recipient_id: selectedClient.id,
        product_name: clientInvForm.product_name,
        billing_cycle: clientInvForm.billing_cycle,
        quantity: Number(clientInvForm.quantity) || 1,
        amount: Number(clientInvForm.amount),
        payment_status: clientInvForm.payment_status,
        notes: clientInvForm.notes,
      });
      // Send immediately
      await axios.post(`${API}/reseller/invoices/${res.data.id}/send`).catch(() => {});
      toast.success('Invoice created and sent');
      setClientInvOpen(false); setSelectedClient(null); setClientSearch('');
      setClientInvForm(EMPTY_INV_FORM); setClientFilteredProducts([]);
      fetchAllInvoices();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create invoice');
    } finally { setCreatingClientInv(false); }
  };

  // === P3: Create CP invoice ONLY ===
  const handleCreateCpInvoiceOnly = async () => {
    if (!selectedCP || !selectedCPClient || !cpInvForm.product_name || !cpInvForm.amount) {
      toast.error('Please fill in all required fields'); return;
    }
    setCreatingCpInv(true);
    try {
      await axios.post(`${API}/reseller/invoices`, {
        invoice_type: 'channel_partner', recipient_id: selectedCP.id,
        cp_client_id: selectedCPClient.id,
        product_name: cpInvForm.product_name,
        billing_cycle: cpInvForm.billing_cycle,
        quantity: Number(cpInvForm.quantity) || 1,
        amount: Number(cpInvForm.amount),
        payment_status: cpInvForm.payment_status,
        notes: cpInvForm.notes,
      });
      toast.success('Invoice created (not yet sent)');
      setCpInvOpen(false); setSelectedCP(null); setSelectedCPClient(null);
      setCpSearch(''); setCpInvStep(1); setCpInvForm(EMPTY_INV_FORM); setCpFilteredProducts([]);
      fetchAllInvoices();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create invoice');
    } finally { setCreatingCpInv(false); }
  };

  const handleCreateCpInvoice = async () => {
    if (!selectedCP || !selectedCPClient || !cpInvForm.product_name || !cpInvForm.amount) {
      toast.error('Please fill in all required fields'); return;
    }
    setCreatingCpInv(true);
    try {
      const res = await axios.post(`${API}/reseller/invoices`, {
        invoice_type: 'channel_partner', recipient_id: selectedCP.id,
        cp_client_id: selectedCPClient.id,
        product_name: cpInvForm.product_name,
        billing_cycle: cpInvForm.billing_cycle,
        quantity: Number(cpInvForm.quantity) || 1,
        amount: Number(cpInvForm.amount),
        payment_status: cpInvForm.payment_status,
        notes: cpInvForm.notes,
      });
      await axios.post(`${API}/reseller/invoices/${res.data.id}/send`).catch(() => {});
      toast.success('Channel Partner invoice created and sent');
      setCpInvOpen(false); setSelectedCP(null); setSelectedCPClient(null);
      setCpSearch(''); setCpInvStep(1); setCpInvForm(EMPTY_INV_FORM); setCpFilteredProducts([]);
      fetchAllInvoices();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create invoice');
    } finally { setCreatingCpInv(false); }
  };

  // === P3: Send existing invoice from list ===
  const handleSendInvoice = async (invoiceId) => {
    setSendingInvId(invoiceId);
    try {
      const res = await axios.post(`${API}/reseller/invoices/${invoiceId}/send`);
      toast.success(`Invoice sent to ${res.data.sent_to}`);
      fetchAllInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invoice');
    } finally { setSendingInvId(null); }
  };

  const getCurrentInvoices = () => {
    if (activeTab === 'my') return myInvoices;
    if (activeTab === 'channel') return channelPartnerInvoices;
    return clientInvoices;
  };

  const currentInvoices = getCurrentInvoices();

  // Invoice counts
  const invoiceCounts = {
    paid: currentInvoices.filter(i => i.status === 'paid').length,
    unpaid: currentInvoices.filter(i => i.status === 'unpaid' || i.status === 'pending').length,
    cancelled: currentInvoices.filter(i => i.status === 'cancelled' || i.status === 'voided').length,
    refunded: currentInvoices.filter(i => i.status === 'refunded').length
  };

  const unpaidInvoices = currentInvoices.filter(i => i.status === 'unpaid' || i.status === 'pending');

  // Filtered invoices
  const filteredInvoices = currentInvoices.filter(invoice => {
    const matchesSearch = invoice.invoice_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         invoice.product_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'paid' && invoice.status === 'paid') ||
                         (statusFilter === 'unpaid' && (invoice.status === 'unpaid' || invoice.status === 'pending')) ||
                         (statusFilter === 'cancelled' && (invoice.status === 'cancelled' || invoice.status === 'voided')) ||
                         (statusFilter === 'refunded' && invoice.status === 'refunded');
    return matchesSearch && matchesStatus;
  });

  const viewInvoice = async (invoiceId) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}`);
      setSelectedInvoice(response.data);
      setViewOpen(true);
    } catch (error) {
      toast.error('Failed to fetch invoice details');
    }
  };

  const handlePayInvoice = async (invoice) => {
    try {
      // Create Razorpay order for the invoice
      const orderRes = await axios.post(`${API}/invoices/${invoice.id}/pay`);
      const { order_id, amount_paise, currency, key_id, user: rpUser } = orderRes.data;

      // Load Razorpay SDK
      if (!window.Razorpay) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.onload = resolve;
          script.onerror = reject;
          document.body.appendChild(script);
        });
      }

      const options = {
        key: key_id,
        amount: amount_paise,
        currency,
        name: 'CloudSetu',
        description: `Invoice ${invoice.invoice_number}`,
        order_id,
        prefill: { name: rpUser?.name, email: rpUser?.email, contact: rpUser?.phone },
        theme: { color: '#0ea5e9' },
        handler: async (response) => {
          try {
            await axios.post(`${API}/invoices/${invoice.id}/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            toast.success(`Payment successful! Invoice ${invoice.invoice_number} is now paid. Admin will provision your service shortly.`);
            fetchAllInvoices();
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Payment verification failed');
          }
        }
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    }
  };

  const downloadInvoice = async (invoiceId, invoiceNumber) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/download`);
      const byteCharacters = atob(response.data.pdf_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to download invoice';
      toast.error(msg);
    }
  };

  const viewInvoiceDetails = async (invoice) => {
    setSelectedInvoice(invoice);
    setViewOpen(true);
    try {
      const res = await axios.get(`${API}/invoices/${invoice.id}/view-details`);
      setSelectedInvoice(prev => ({ ...prev, ...res.data }));
    } catch (_) { /* use existing invoice data if endpoint fails */ }
  };

  const handleUpdateInvoiceStatus = async (invoice, newStatus) => {
    try {
      await axios.put(`${API}/reseller/invoices/${invoice.id}/status`, { status: newStatus });
      toast.success(`Invoice marked as ${newStatus}`);
      fetchAllInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: { bg: 'bg-green-500', text: 'text-white' },
      unpaid: { bg: 'bg-red-500', text: 'text-white' },
      pending: { bg: 'bg-amber-500', text: 'text-white' },
      cancelled: { bg: 'bg-slate-500', text: 'text-white' },
      voided: { bg: 'bg-slate-500', text: 'text-white' },
      refunded: { bg: 'bg-blue-500', text: 'text-white' }
    };
    const style = styles[status] || styles.pending;
    return (
      <span className={`px-3 py-1 rounded text-xs font-medium ${style.bg} ${style.text}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getTabLabel = () => {
    if (activeTab === 'my') return 'My Invoices';
    if (activeTab === 'channel') return 'Channel Partner Invoices';
    return 'Client Invoices';
  };

  return (
    <DashboardLayout role={user?.role}>
      <div className="flex gap-6">
        {/* Left Sidebar */}
        <div className="w-64 flex-shrink-0 space-y-4">
          {/* Invoices Due Card */}
          <Card className="bg-white border shadow-sm">
            <CardContent className="p-0">
              <button 
                className="w-full flex items-center justify-between p-4 hover:bg-slate-50"
                onClick={() => setInvoicesDueExpanded(!invoicesDueExpanded)}
              >
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-slate-600" />
                  <span className="font-medium text-slate-700">{unpaidInvoices.length} Invoices Due</span>
                </div>
                {invoicesDueExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              
              {invoicesDueExpanded && (
                <div className="border-t px-4 py-3">
                  <p className="text-sm text-slate-500">
                    {unpaidInvoices.length === 0 
                      ? "You have no unpaid invoices at this time."
                      : `You have ${unpaidInvoices.length} unpaid invoice(s).`}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Status Filter Card */}
          <Card className="bg-white border shadow-sm">
            <CardContent className="p-0">
              <button 
                className="w-full flex items-center justify-between p-4 hover:bg-slate-50"
                onClick={() => setStatusExpanded(!statusExpanded)}
              >
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-slate-600" />
                  <span className="font-medium text-slate-700">Status</span>
                </div>
                {statusExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              
              {statusExpanded && (
                <div className="border-t">
                  {[
                    { key: 'all', label: 'All', count: currentInvoices.length },
                    { key: 'paid', label: 'Paid', count: invoiceCounts.paid },
                    { key: 'unpaid', label: 'Unpaid', count: invoiceCounts.unpaid },
                    { key: 'cancelled', label: 'Cancelled', count: invoiceCounts.cancelled },
                    { key: 'refunded', label: 'Refunded', count: invoiceCounts.refunded }
                  ].map((item) => (
                    <button
                      key={item.key}
                      className={`w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 text-left border-b last:border-b-0 ${
                        statusFilter === item.key ? 'bg-blue-50 text-blue-700' : ''
                      }`}
                      onClick={() => setStatusFilter(item.key)}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          statusFilter === item.key ? 'border-blue-600' : 'border-slate-300'
                        }`}>
                          {statusFilter === item.key && (
                            <span className="w-2 h-2 rounded-full bg-blue-600"></span>
                          )}
                        </span>
                        <span className="text-sm">{item.label}</span>
                      </div>
                      <span className="text-sm font-medium text-slate-500">{item.count}</span>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Billing Navigation Card */}
          <Card className="bg-white border shadow-sm">
            <CardContent className="p-0">
              <button 
                className="w-full flex items-center justify-between p-4 hover:bg-slate-50"
                onClick={() => setBillingExpanded(!billingExpanded)}
              >
                <div className="flex items-center gap-2">
                  <Landmark className="h-4 w-4 text-slate-600" />
                  <span className="font-medium text-slate-700">Invoices</span>
                </div>
                {billingExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              
              {billingExpanded && (
                <div className="border-t">
                  <button
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b ${
                      activeTab === 'my' ? 'bg-slate-600 text-white' : 'hover:bg-slate-50'
                    }`}
                    onClick={() => { setActiveTab('my'); setStatusFilter('all'); }}
                    data-testid="tab-my-invoices"
                  >
                    <FileText className="h-4 w-4" />
                    <span className="text-sm">My Invoices</span>
                  </button>
                  {isSynergyPartner && (
                    <button
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b ${
                        activeTab === 'channel' ? 'bg-slate-600 text-white' : 'hover:bg-slate-50'
                      }`}
                      onClick={() => { setActiveTab('channel'); setStatusFilter('all'); }}
                      data-testid="tab-channel-invoices"
                    >
                      <Building className="h-4 w-4" />
                      <span className="text-sm">Channel Partner Invoices</span>
                    </button>
                  )}
                  {/* Client Invoices tab — SP only (not CP) */}
                  {isSynergyPartner && (
                  <button
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left ${
                      activeTab === 'clients' ? 'bg-slate-600 text-white' : 'hover:bg-slate-50'
                    }`}
                    onClick={() => { setActiveTab('clients'); setStatusFilter('all'); }}
                    data-testid="tab-client-invoices"
                  >
                    <Users className="h-4 w-4" />
                    <span className="text-sm">Client Invoices</span>
                  </button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-800">{getTabLabel()}</h2>
              <p className="text-sm text-slate-500">
                {activeTab === 'my' && 'Invoices billed to you by Admin'}
                {activeTab === 'channel' && 'Invoices you billed to Channel Partners'}
                {activeTab === 'clients' && 'Invoices for your clients'}
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap justify-end">
              <button onClick={fetchAllInvoices} className="p-2 rounded-lg hover:bg-slate-100 border border-slate-200" title="Refresh" data-testid="refresh-btn"><RefreshCw className="h-4 w-4 text-slate-500" /></button>
              {isSynergyPartner && (
                <div className="flex items-center gap-2">
                  <Button
                    className="bg-blue-600 hover:bg-blue-700 text-white text-sm h-9"
                    onClick={() => { setClientInvOpen(true); setSelectedClient(null); setClientSearch(''); setClientResults([]); }}
                    data-testid="create-client-invoice-btn"
                  >
                    <Users className="h-4 w-4 mr-1" />
                    Create Client Invoice
                  </Button>
                  <Button
                    className="bg-purple-600 hover:bg-purple-700 text-white text-sm h-9"
                    onClick={() => { setCpInvOpen(true); setSelectedCP(null); setCpSearch(''); setCpResults([]); setSelectedCPClient(null); setCpInvStep(1); }}
                    data-testid="create-cp-invoice-btn"
                  >
                    <Building className="h-4 w-4 mr-1" />
                    Create Partner Invoice
                  </Button>
                </div>
              )}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search invoices..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-48"
                  data-testid="search-invoices"
                />
              </div>
            </div>
          </div>

          {/* Invoices Table */}
          <Card className="bg-white border shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-600 text-nowrap">Invoice #</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-600 text-nowrap">
                      {activeTab === 'my' ? 'Product' : 'Billed To'}
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-600 text-nowrap">Date</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-600 text-nowrap">Amount (+ GST)</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-600 text-nowrap">Status</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-600 text-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center">
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                      </td>
                    </tr>
                  ) : filteredInvoices.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-slate-500">
                        <FileText className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                        <p className="font-medium">No invoices found</p>
                      </td>
                    </tr>
                  ) : (
                    filteredInvoices.map((invoice) => (
                      <tr 
                        key={invoice.id} 
                        className="border-b last:border-b-0 hover:bg-slate-50 transition-colors"
                      >
                        <td className="px-6 py-4 text-nowrap">
                          <span className="font-medium text-slate-800">{invoice.invoice_number}</span>
                        </td>
                        <td className="px-6 py-4 text-nowrap">
                          <span className="text-sm text-slate-700">
                            {activeTab === 'my' 
                              ? invoice.product_name 
                              : (invoice.client_name || invoice.partner_name || invoice.product_name)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-nowrap">
                          <span className="text-sm text-slate-700">{formatDate(invoice.created_at)}</span>
                        </td>
                        <td className="px-6 py-4 text-nowrap">
                          <span className="text-sm font-medium text-slate-800">₹{invoice.amount?.toLocaleString('en-IN', {minimumFractionDigits:2})}</span>
                          {(invoice.subtotal != null && invoice.gst_amount != null) && (
                            <p className="text-xs text-slate-400 mt-0.5">
                              ₹{Number(invoice.subtotal).toLocaleString('en-IN', {minimumFractionDigits:2})} + ₹{Number(invoice.gst_amount).toLocaleString('en-IN', {minimumFractionDigits:2})} GST
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-4 text-nowrap">
                          <div className="flex items-center gap-1.5">
                            {getStatusBadge(invoice.status)}
                            <QueryBadge invoice={invoice} />
                          </div>
                        </td>
                        <td className="px-6 py-4 text-nowrap">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {/* Task 7: Pay Invoice for SP's own pending invoices (admin created them) */}
                            {activeTab === 'my' && (invoice.status === 'pending' || invoice.status === 'unpaid') && !invoice.created_by_sp && (
                              <Button
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700 text-white h-7 text-xs px-2"
                                onClick={() => handlePayInvoice(invoice)}
                                data-testid={`sp-pay-invoice-${invoice.id}`}
                              >
                                <CreditCard className="h-3 w-3 mr-1" />Pay Invoice
                              </Button>
                            )}
                            {/* Task 5: SP status toggle for SP-created invoices — NOT on "My Invoices" tab */}
                            {invoice.created_by_sp && isSynergyPartner && activeTab !== 'my' && (
                              <select
                                className="h-7 text-xs border border-slate-200 rounded px-1.5 bg-white cursor-pointer"
                                value={invoice.status}
                                onChange={e => handleUpdateInvoiceStatus(invoice, e.target.value)}
                                data-testid={`invoice-status-toggle-${invoice.id}`}
                              >
                                <option value="pending">Pending</option>
                                <option value="paid">Paid</option>
                              </select>
                            )}
                            {/* P3: Send button for SP-created invoices not yet sent */}
                            {invoice.created_by_sp && isSynergyPartner && activeTab !== 'my' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className={`h-7 px-2 text-xs ${invoice.is_sent ? 'text-slate-400 border-slate-200' : 'text-blue-600 border-blue-200 hover:bg-blue-600 hover:border-blue-600 hover:text-white'}`}
                                onClick={() => handleSendInvoice(invoice.id)}
                                disabled={sendingInvId === invoice.id}
                                data-testid={`send-invoice-${invoice.id}`}
                                title={invoice.is_sent ? 'Re-send invoice' : 'Send invoice to recipient'}
                              >
                                {sendingInvId === invoice.id
                                  ? <span className="animate-spin">⋯</span>
                                  : <><Send className="h-3 w-3 mr-1" />{invoice.is_sent ? 'Re-send' : 'Send'}</>
                                }
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="h-7 w-7 p-0"
                              onClick={() => viewInvoiceDetails(invoice)}
                              data-testid={`view-invoice-${invoice.id}`}
                              title="View Invoice Details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <RaiseQueryButton invoice={invoice} compact onQueryRaised={() => {}} />
                            {invoice.status === 'paid' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="h-7 w-7 p-0 text-green-600 border-green-200 hover:bg-green-600 hover:border-green-600"
                              onClick={() => downloadInvoice(invoice.id, invoice.invoice_number)}
                              data-testid={`download-invoice-${invoice.id}`}
                              title="Download PDF"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            <div className="flex items-center justify-between px-6 py-4 border-t bg-slate-50">
              <p className="text-sm text-slate-600">
                Showing {filteredInvoices.length} of {currentInvoices.length} invoices
              </p>
              <div className="flex items-center gap-1">
                <button className="px-3 py-1 text-sm border rounded hover:bg-slate-100">Previous</button>
                <button className="px-3 py-1 text-sm bg-slate-700 text-white rounded">1</button>
                <button className="px-3 py-1 text-sm border rounded hover:bg-slate-100">Next</button>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Invoice View Dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Invoice #{selectedInvoice?.invoice_number}</DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4 py-2">
              {/* Status */}
              <div className="flex items-center justify-between">
                {getStatusBadge(selectedInvoice.status)}
                <span className="text-xs text-slate-400">{formatDate(selectedInvoice.created_at)}</span>
              </div>

              {/* Bill To */}
              {selectedInvoice.bill_to_company && (
                <div className="bg-slate-50 rounded-lg p-3 text-sm">
                  <p className="font-semibold text-slate-700 mb-1">Invoiced To</p>
                  <p className="font-medium">{selectedInvoice.bill_to_company}</p>
                  <p className="text-slate-500">{selectedInvoice.bill_to_attn}</p>
                  {selectedInvoice.bill_to_address && <p className="text-slate-500">{selectedInvoice.bill_to_address}</p>}
                  {selectedInvoice.bill_to_city && <p className="text-slate-500">{[selectedInvoice.bill_to_city, selectedInvoice.bill_to_state, selectedInvoice.bill_to_pincode].filter(Boolean).join(', ')}</p>}
                  {selectedInvoice.bill_to_gst && <p className="text-slate-500 font-mono">GSTIN: {selectedInvoice.bill_to_gst}</p>}
                </div>
              )}

              {/* Product */}
              <div className="border-t pt-3">
                <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Service</p>
                <p className="font-medium">{selectedInvoice.product_name}</p>
                {selectedInvoice.billing_cycle && (
                  <p className="text-sm text-slate-500">{selectedInvoice.billing_cycle?.replace(/_/g, ' ')}</p>
                )}
              </div>

              {/* GST Breakdown */}
              <div className="border-t pt-3 space-y-1 text-sm">
                {(() => {
                  const gstType = selectedInvoice.gst_type || 'IGST';
                  const gstAmt = selectedInvoice.gst_amount || 0;
                  const half = Math.round(gstAmt / 2 * 100) / 100;
                  const entityState = selectedInvoice.entity_state_at_time_of_creation || '';
                  return (
                    <>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Sub Total</span>
                        <span>₹{(selectedInvoice.subtotal || selectedInvoice.amount)?.toLocaleString('en-IN', {minimumFractionDigits:2})}</span>
                      </div>
                      {gstType === 'CGST+SGST' ? (
                        <>
                          <div className="flex justify-between">
                            <span className="text-slate-500">CGST (9%)</span>
                            <span>₹{half?.toLocaleString('en-IN', {minimumFractionDigits:2})}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">SGST (9%)</span>
                            <span>₹{half?.toLocaleString('en-IN', {minimumFractionDigits:2})}</span>
                          </div>
                        </>
                      ) : (
                        <div className="flex justify-between">
                          <span className="text-slate-500">IGST (18%)</span>
                          <span>₹{gstAmt?.toLocaleString('en-IN', {minimumFractionDigits:2})}</span>
                        </div>
                      )}
                      {selectedInvoice.credit > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span>Credit</span>
                          <span>-₹{selectedInvoice.credit?.toLocaleString('en-IN', {minimumFractionDigits:2})}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-semibold text-base border-t pt-1 mt-1">
                        <span>Total</span>
                        <span>₹{(selectedInvoice.total || selectedInvoice.amount)?.toLocaleString('en-IN', {minimumFractionDigits:2})}</span>
                      </div>
                    </>
                  );
                })()}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                {selectedInvoice.status !== 'paid' && (
                  <span className="text-xs text-amber-600 bg-amber-50 px-3 py-1.5 rounded-full border border-amber-200">
                    PDF available once payment is confirmed
                  </span>
                )}
                {selectedInvoice.status === 'paid' && (
                  <Button size="sm" onClick={() => downloadInvoice(selectedInvoice.id, selectedInvoice.invoice_number)}
                    className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="dialog-download-pdf">
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Client Invoice Dialog */}
      <Dialog open={clientInvOpen} onOpenChange={open => { setClientInvOpen(open); if (!open) { setClientFilteredProducts([]); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-blue-600" />Create Client Invoice</DialogTitle>
            <DialogDescription>Search for a direct client and create an invoice for them.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Client Search */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Search Client (by name, email, or ID) *</label>
              <div className="relative">
                <Input placeholder="Type to search..." value={clientSearch}
                  onChange={e => handleClientSearch(e.target.value)} data-testid="client-inv-search" />
                {clientResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-slate-200 rounded-md shadow-lg z-50 max-h-40 overflow-y-auto">
                    {clientResults.map(c => (
                      <div key={c.id} className="px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm border-b last:border-0"
                        onClick={() => { setSelectedClient(c); setClientSearch(c.company_name || c.email); setClientResults([]); }}>
                        <span className="font-medium">{c.company_name || 'N/A'}</span>
                        <span className="text-slate-400 ml-2 text-xs">{c.email}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {selectedClient && (
                <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-800 flex items-center gap-1">
                  <Users className="h-3 w-3" /> Selected: <strong>{selectedClient.company_name || selectedClient.email}</strong>
                </div>
              )}
            </div>
            {/* Category → Product dropdowns */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Category</label>
                <select className="w-full border border-slate-200 rounded-md text-sm px-3 py-2 bg-white"
                  value={clientInvForm.category_id}
                  onChange={e => handleCategoryChange(e.target.value, setClientInvForm, setClientFilteredProducts)}
                  data-testid="client-inv-category">
                  <option value="">-- Select Category --</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Product (auto-fill price)</label>
                <select className="w-full border border-slate-200 rounded-md text-sm px-3 py-2 bg-white"
                  value={clientInvForm.product_id}
                  onChange={e => handleProductChange(e.target.value, setClientInvForm, clientInvForm.billing_cycle)}
                  disabled={!clientInvForm.category_id}
                  data-testid="client-inv-product-dropdown">
                  <option value="">-- Select Product --</option>
                  {clientFilteredProducts.map(p => <option key={p.id || p._id} value={p.id || p._id}>{p.name}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Billing Cycle</label>
                <select className="w-full border border-slate-200 rounded-md text-sm px-3 py-2 bg-white"
                  value={clientInvForm.billing_cycle}
                  onChange={e => {
                    const newCycle = e.target.value;
                    setClientInvForm(f => {
                      const prod = clientInvForm.product_id ? allProducts.find(p => (p.id || p._id) === clientInvForm.product_id) : null;
                      const cycleKey = getBillingCycleKey(newCycle);
                      const price = prod ? (getSpSellingPrice(prod.id || prod._id, cycleKey) ?? prod.billing_prices?.[cycleKey] ?? prod.base_price ?? f.amount) : f.amount;
                      return { ...f, billing_cycle: newCycle, amount: prod ? String(price) : f.amount };
                    });
                  }}
                  data-testid="client-inv-billing">
                  {BILLING_CYCLES.filter(c => {
                    if (!clientInvForm.product_id) return true;
                    const prod = allProducts.find(p => (p.id || p._id) === clientInvForm.product_id);
                    const enabled = prod?.enabled_billing_cycles;
                    if (!enabled || enabled.length === 0) return true;
                    return enabled.includes(BILLING_CYCLE_DISPLAY_TO_KEY[c] || c);
                  }).map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Quantity</label>
                <Input type="number" min={1} value={clientInvForm.quantity}
                  onChange={e => setClientInvForm(f => ({ ...f, quantity: e.target.value }))} data-testid="client-inv-qty" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Base Selling Price (excl. GST) ₹ *</label>
              <Input type="number" min={0} step="0.01" placeholder="0.00" value={clientInvForm.amount}
                onChange={e => setClientInvForm(f => ({ ...f, amount: e.target.value }))} data-testid="client-inv-amount" />
              {clientInvForm.amount > 0 && (
                <div className="mt-1.5 text-xs bg-slate-50 border border-slate-100 rounded px-2 py-1.5 text-slate-600 flex flex-wrap gap-x-2">
                  <span>Base: <strong>₹{Number(clientInvForm.amount).toLocaleString('en-IN',{minimumFractionDigits:2})}</strong></span>
                  <span className="text-slate-400">+</span>
                  <span>GST 18%: <strong>₹{(Number(clientInvForm.amount)*0.18).toLocaleString('en-IN',{minimumFractionDigits:2})}</strong></span>
                  <span className="text-slate-400">=</span>
                  <span className="text-green-700 font-bold">Total: ₹{(Number(clientInvForm.amount)*1.18).toLocaleString('en-IN',{minimumFractionDigits:2})}</span>
                </div>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Notes (optional)</label>
              <textarea className="w-full border border-slate-200 rounded-md text-sm px-3 py-2 min-h-[60px] resize-none"
                value={clientInvForm.notes} onChange={e => setClientInvForm(f => ({ ...f, notes: e.target.value }))} data-testid="client-inv-notes" />
            </div>
          </div>
          <DialogFooter className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setClientInvOpen(false)}>Cancel</Button>
            {/* P3 Split buttons */}
            <Button variant="outline" className="border-slate-300 text-slate-700"
              onClick={handleCreateClientInvoiceOnly} disabled={creatingClientInv} data-testid="create-only-client-invoice">
              <Plus className="h-4 w-4 mr-1" />Create Invoice
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={handleCreateClientInvoice} disabled={creatingClientInv} data-testid="create-send-client-invoice">
              {creatingClientInv ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" /> : <Send className="h-4 w-4 mr-1" />}
              Create & Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CP Invoice Dialog */}
      <Dialog open={cpInvOpen} onOpenChange={(open) => { setCpInvOpen(open); if (!open) { setSelectedCP(null); setSelectedCPClient(null); setCpInvStep(1); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Building className="h-5 w-5 text-purple-600" />Create Channel Partner Invoice</DialogTitle>
            <DialogDescription>
              {cpInvStep === 1 ? 'Step 1: Search and select a Channel Partner.' : `Step 2: Select the client under ${selectedCP?.company_name || 'CP'} and fill in invoice details.`}
            </DialogDescription>
          </DialogHeader>

          {cpInvStep === 1 ? (
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Search Channel Partner (by name, email, or ID) *</label>
                <div className="relative">
                  <Input placeholder="Type to search..." value={cpSearch} onChange={e => handleCpSearch(e.target.value)} data-testid="cp-inv-search" />
                  {cpResults.length > 0 && (
                    <div className="absolute top-full left-0 right-0 bg-white border border-slate-200 rounded-md shadow-lg z-50 max-h-40 overflow-y-auto">
                      {cpResults.map(cp => (
                        <div key={cp.id} className="px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm border-b last:border-0" onClick={() => handleSelectCP(cp)}>
                          <span className="font-medium">{cp.company_name || 'N/A'}</span>
                          <span className="text-slate-400 ml-2 text-xs">{cp.email}</span>
                          {cp.account_id && <span className="text-slate-400 ml-1 text-xs">· {cp.account_id}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {cpResults.length === 0 && cpSearch.length >= 2 && (
                <p className="text-sm text-slate-500">No Channel Partners found for "{cpSearch}"</p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Selected CP banner */}
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm">
                <p className="font-medium text-purple-800"><Building className="h-3.5 w-3.5 inline mr-1" />{selectedCP?.company_name || selectedCP?.email}</p>
                <p className="text-purple-600 text-xs mt-0.5">{selectedCP?.email} · {selectedCP?.account_id || 'No ID'}</p>
                <button className="text-xs text-purple-500 underline mt-1" onClick={() => { setSelectedCP(null); setCpInvStep(1); setCpSearch(''); setSelectedCPClient(null); }}>Change CP</button>
              </div>

              {/* Client under CP */}
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Select Client under this CP *</label>
                {cpClientResults.length === 0 ? (
                  <p className="text-sm text-slate-500 italic">This Channel Partner has no clients yet.</p>
                ) : (
                  <select className="w-full border border-slate-200 rounded-md text-sm px-3 py-2 bg-white" value={selectedCPClient?.id || ''} onChange={e => setSelectedCPClient(cpClientResults.find(c => c.id === e.target.value) || null)} data-testid="cp-inv-client-select">
                    <option value="">Select client...</option>
                    {cpClientResults.map(c => (
                      <option key={c.id} value={c.id}>{c.company_name || c.email} ({c.email})</option>
                    ))}
                  </select>
                )}
                {selectedCPClient && (
                  <div className="mt-1.5 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-800">
                    Invoice will reference: <strong>{selectedCPClient.company_name || selectedCPClient.email}</strong> as the end client.
                  </div>
                )}
              </div>

              {/* Category → Product dropdowns for CP invoice */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Category</label>
                  <select className="w-full border border-slate-200 rounded-md text-sm px-3 py-2 bg-white"
                    value={cpInvForm.category_id}
                    onChange={e => handleCategoryChange(e.target.value, setCpInvForm, setCpFilteredProducts)}
                    data-testid="cp-inv-category">
                    <option value="">-- Select Category --</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Product (auto-fill price)</label>
                  <select className="w-full border border-slate-200 rounded-md text-sm px-3 py-2 bg-white"
                    value={cpInvForm.product_id}
                    onChange={e => handleProductChange(e.target.value, setCpInvForm, cpInvForm.billing_cycle)}
                    disabled={!cpInvForm.category_id}
                    data-testid="cp-inv-product-dropdown">
                    <option value="">-- Select Product --</option>
                    {cpFilteredProducts.map(p => <option key={p.id || p._id} value={p.id || p._id}>{p.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Product / Service Name *</label>
                <Input placeholder="Auto-filled when product selected, or type custom name"
                  value={cpInvForm.product_name} onChange={e => setCpInvForm(f => ({ ...f, product_name: e.target.value }))} data-testid="cp-inv-product" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Billing Cycle</label>
                  <select className="w-full border border-slate-200 rounded-md text-sm px-3 py-2 bg-white"
                    value={cpInvForm.billing_cycle}
                    onChange={e => {
                      const newCycle = e.target.value;
                      setCpInvForm(f => {
                        const prod = cpInvForm.product_id ? allProducts.find(p => (p.id || p._id) === cpInvForm.product_id) : null;
                        const cycleKey = getBillingCycleKey(newCycle);
                        const price = prod ? (getSpSellingPrice(prod.id || prod._id, cycleKey) ?? prod.billing_prices?.[cycleKey] ?? prod.base_price ?? f.amount) : f.amount;
                        return { ...f, billing_cycle: newCycle, amount: prod ? String(price) : f.amount };
                      });
                    }}
                    data-testid="cp-inv-billing">
                    {BILLING_CYCLES.filter(c => {
                      if (!cpInvForm.product_id) return true;
                      const prod = allProducts.find(p => (p.id || p._id) === cpInvForm.product_id);
                      const enabled = prod?.enabled_billing_cycles;
                      if (!enabled || enabled.length === 0) return true;
                      return enabled.includes(BILLING_CYCLE_DISPLAY_TO_KEY[c] || c);
                    }).map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Quantity</label>
                  <Input type="number" min={1} value={cpInvForm.quantity} onChange={e => setCpInvForm(f => ({ ...f, quantity: e.target.value }))} data-testid="cp-inv-qty" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Base Selling Price (excl. GST) ₹ *</label>
                <Input type="number" min={0} step="0.01" placeholder="0.00" value={cpInvForm.amount} onChange={e => setCpInvForm(f => ({ ...f, amount: e.target.value }))} data-testid="cp-inv-amount" />
                {cpInvForm.amount > 0 && (
                  <div className="mt-1.5 text-xs bg-slate-50 border border-slate-100 rounded px-2 py-1.5 text-slate-600 flex flex-wrap gap-x-2">
                    <span>Base: <strong>₹{Number(cpInvForm.amount).toLocaleString('en-IN',{minimumFractionDigits:2})}</strong></span>
                    <span className="text-slate-400">+</span>
                    <span>GST 18%: <strong>₹{(Number(cpInvForm.amount)*0.18).toLocaleString('en-IN',{minimumFractionDigits:2})}</strong></span>
                    <span className="text-slate-400">=</span>
                    <span className="text-green-700 font-bold">Total: ₹{(Number(cpInvForm.amount)*1.18).toLocaleString('en-IN',{minimumFractionDigits:2})}</span>
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Notes (optional)</label>
                <textarea className="w-full border border-slate-200 rounded-md text-sm px-3 py-2 min-h-[60px] resize-none" value={cpInvForm.notes} onChange={e => setCpInvForm(f => ({ ...f, notes: e.target.value }))} data-testid="cp-inv-notes" />
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => { setCpInvOpen(false); setSelectedCP(null); setCpInvStep(1); setCpFilteredProducts([]); }}>Cancel</Button>
            {cpInvStep === 2 && (
              <>
                <Button variant="outline" className="border-slate-300 text-slate-700"
                  onClick={handleCreateCpInvoiceOnly} disabled={creatingCpInv || !selectedCPClient} data-testid="create-only-cp-invoice">
                  <Plus className="h-4 w-4 mr-1" />Create Invoice
                </Button>
                <Button className="bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={handleCreateCpInvoice} disabled={creatingCpInv || !selectedCPClient} data-testid="confirm-cp-invoice">
                  {creatingCpInv ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" /> : <Send className="h-4 w-4 mr-1" />}
                  Create & Send
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
